//*************************************************************************************
//* WaMS Component Framework - app.session-timeout.js
//* Warns the user before the sliding-expiration auth cookie lapses and offers a way
//* to renew it without losing the current page - either silently (KeepAlive ajax call)
//* or, if the cookie already fully expired, via a popup login that self-closes.
//*************************************************************************************
import $ from 'jquery';
import { util } from './app.js';

const WARNING_MINUTES_BEFORE_EXPIRY = 5;
const LOGIN_MESSAGE = 'wams-login-complete';
const COUNTDOWN_ELEMENT_ID = 'session-countdown';

let warningTimer = null;
let expiryTimer = null;
let countdownInterval = null;
let sessionExpiresAt = null;
let sessionExpired = false;
let activeWarningToast = null;
let activeReauthToast = null;
let popupInFlight = false;
let messageListenerInstalled = false;

// Every tab shares the same auth cookie, so any tab's ajax activity already renews it
// server-side. Without this, an idle tab would still show its own warning even though
// another tab kept the session alive - broadcast renewals so siblings can defer theirs.
const channel = ('BroadcastChannel' in window) ? new BroadcastChannel('wams-session-timeout') : null;

function appPath() {
    return window.app.page.appPath;
}

function getAntiForgeryToken() {
    return $('input[name="__RequestVerificationToken"]').first().val();
}

function scheduleWarning() {
    if (warningTimer) {
        clearTimeout(warningTimer);
    }
    if (expiryTimer) {
        clearTimeout(expiryTimer);
    }
    sessionExpired = false;

    const sessionMinutes = window.sessionExpireMinutes || 60;
    sessionExpiresAt = Date.now() + sessionMinutes * 60 * 1000;

    const delayMinutes = Math.max(sessionMinutes - WARNING_MINUTES_BEFORE_EXPIRY, 1);
    warningTimer = setTimeout(showWarning, delayMinutes * 60 * 1000);
    expiryTimer = setTimeout(showExpired, sessionMinutes * 60 * 1000);
}

function formatRemaining(ms) {
    const totalSeconds = Math.max(Math.round(ms / 1000), 0);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes + ':' + String(seconds).padStart(2, '0');
}

function startCountdown() {
    stopCountdown();
    countdownInterval = setInterval(function () {
        const el = document.getElementById(COUNTDOWN_ELEMENT_ID);
        if (!el) {
            return;
        }
        el.textContent = formatRemaining(sessionExpiresAt - Date.now());
    }, 1000);
}

function stopCountdown() {
    if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
    }
}

function showWarning() {
    if (!window.toast || sessionExpired) {
        return;
    }
    activeWarningToast = window.toast.warning(
        'Your session will time out in <span id="' + COUNTDOWN_ELEMENT_ID + '">' +
            formatRemaining(sessionExpiresAt - Date.now()) + '</span>. Click this message to stay signed in.',
        'Session Expiring',
        {
            timeOut: 0,
            extendedTimeOut: 0,
            closeButton: true,
            onclick: renewSession
        }
    );
    startCountdown();
}

// The warning toast above assumes the user is still around to click it. If nothing
// renews the session before the cookie actually lapses, swap it for a message that
// reflects reality instead of leaving a stale "time out soon" toast up indefinitely.
function showExpired() {
    if (sessionExpired) {
        return;
    }
    sessionExpired = true;
    stopCountdown();
    if (activeWarningToast && activeWarningToast.toastInstance) {
        activeWarningToast.toastInstance.hide();
    }
    activeWarningToast = null;

    if (!window.toast) {
        return;
    }
    activeWarningToast = window.toast.warning(
        'Your session has expired. Click this message to sign back in.',
        'Session Expired',
        {
            timeOut: 0,
            extendedTimeOut: 0,
            closeButton: true,
            onclick: promptReauth
        }
    );
}

function dismissWarning() {
    stopCountdown();
    if (activeWarningToast && activeWarningToast.toastInstance) {
        activeWarningToast.toastInstance.hide();
    }
    activeWarningToast = null;
    if (activeReauthToast && activeReauthToast.toastInstance) {
        activeReauthToast.toastInstance.hide();
    }
    activeReauthToast = null;
}

function renewSession() {
    $.ajax({
        url: appPath() + '/Account/KeepAlive',
        type: 'POST',
        data: { __RequestVerificationToken: getAntiForgeryToken() },
        success: function () {
            dismissWarning();
            scheduleWarning();
            if (window.toast) {
                window.toast.success('Your session has been extended.', '', { timeOut: 3000 });
            }
        },
        error: function (xhr) {
            if (xhr.status === 401) {
                dismissWarning();
                promptReauth();
            }
        }
    });
}

function installMessageListener() {
    if (messageListenerInstalled) {
        return;
    }
    messageListenerInstalled = true;
    window.addEventListener('message', function (event) {
        if (event.origin !== window.location.origin || event.data !== LOGIN_MESSAGE) {
            return;
        }
        popupInFlight = false;
        onAnyTabRenewed();
        if (channel) {
            channel.postMessage('renew');
        }
        if (window.toast) {
            window.toast.success("You're signed back in!", 'Sign in sucessful', { timeOut: 5000 });
        }
    });
}

// Called on 401s: either the warning went unanswered until the cookie fully lapsed,
// or an ajax call elsewhere on the page hit an expired session mid-request.
function promptReauth() {
    sessionExpired = true;
    if (warningTimer) {
        clearTimeout(warningTimer);
        warningTimer = null;
    }
    if (expiryTimer) {
        clearTimeout(expiryTimer);
        expiryTimer = null;
    }
    stopCountdown();
    if (activeWarningToast && activeWarningToast.toastInstance) {
        activeWarningToast.toastInstance.hide();
    }
    activeWarningToast = null;

    if (popupInFlight) {
        return;
    }
    installMessageListener();

    const returnUrl = encodeURIComponent(appPath() + '/Account/PopupLoginComplete');
    const loginUrl = appPath() + '/Account/Login?ReturnUrl=' + returnUrl;
    const popup = window.open(loginUrl, 'wamsLogin', 'width=500,height=650');

    if (!popup) {
        // Popup blocked - fall back to today's behavior, a same-tab redirect.
        window.location.href = appPath() + '/Account/Login?ReturnUrl=' + encodeURIComponent(window.location.pathname);
        return;
    }

    popupInFlight = true;
    popup.focus();

    if (window.toast) {
        activeReauthToast = window.toast.info('Please finish signing in in the popup window.', 'Session Expired', { timeOut: 0 });
    }

    // If the user closes the popup without completing login, don't leave promptReauth stuck.
    const closeCheck = setInterval(function () {
        if (popup.closed) {
            clearInterval(closeCheck);
            popupInFlight = false;
        }
    }, 1000);
}

function onAnyTabRenewed() {
    dismissWarning();
    scheduleWarning();
}

const sessionTimeout = {
    Init: function () {
        if (!window.sessionTimeoutWarningEnabled) {
            util.console.log('Session timeout monitoring disabled via AppSettings.SessionTimeoutWarningEnabled.');
            return;
        }

        scheduleWarning();

        if (channel) {
            channel.onmessage = function (event) {
                if (event.data === 'renew') {
                    onAnyTabRenewed();
                }
            };
        }

        // Any authenticated ajax call from this tab renews the shared cookie - reset our own
        // timer and let sibling tabs know so they don't warn unnecessarily either.
        $(document).ajaxSend(function () {
            onAnyTabRenewed();
            if (channel) {
                channel.postMessage('renew');
            }
        });

        util.console.log('Session timeout monitoring initialized.');
    },
    promptReauth: promptReauth
};

export default sessionTimeout;
